import * as pako from 'pako';
import notificationService from './notificationService';

// Enhanced WebSocket Service for Real-time Communication - Function-based implementation
// State management using closures
let ws: WebSocket | null = null;
let reconnectAttempts = 0;
const maxReconnectAttempts = 3; // Reduced from 5 to 3
const reconnectDelay = 15000; // Increased from 1000 to 15000ms (15 seconds)
let _isMaintenanceMode = false;
let isConnecting = false;
const messageHandlers: Map<string, Function[]> = new Map();
let token: string | null = null;

// Enhanced features from old frontend
let socketQueue: any[] = [];
let sportsQueue: { [key: string]: any } = {};
let requestProcessed = false;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let workFixtureIds: { [key: string]: boolean } = {};
let heartBitTime = 0;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let addSportsListFlag = true;
let requestSeed = 0;
let reconnectSettings: any = null;
let duplicateConnectionWarning = false;
let maintenanceMode = false;
let binaryType: 'arraybuffer' | 'blob' = 'arraybuffer';
let messageQueueProcessor: NodeJS.Timeout | null = null;
let heartbeatInterval: NodeJS.Timeout | null = null;
let connectionStatusInterval: NodeJS.Timeout | null = null;

// Initialize the service
token = localStorage.getItem('authToken');
startMessageQueueProcessor();

export function connect(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isConnecting || (ws && ws.readyState === WebSocket.OPEN)) {
      resolve();
      return;
    }

    isConnecting = true;
    let wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:3001/stream';
    
    // Force the correct WebSocket URL format (not Socket.IO)
    if (wsUrl.includes('/socket.io/')) {
      console.warn('Detected Socket.IO URL, converting to WebSocket URL');
      wsUrl = wsUrl.replace('/socket.io/', '/stream');
    }
    
    // Make sure it's a proper WebSocket URL
    if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
      console.error('Invalid WebSocket URL format:', wsUrl);
      reject(new Error('Invalid WebSocket URL format'));
      return;
    }
    
    if (token) {
      wsUrl += `?token=${token}`;
    }

    try {
      ws = new WebSocket(wsUrl);
      ws.binaryType = binaryType;
      setupEventHandlers();

      ws.onopen = () => {
        console.log('✅ [WebSocket] Connected successfully');
        isConnecting = false;
        reconnectAttempts = 0;
        heartBitTime = Date.now();
        startHeartbeat();
        startConnectionStatus();
        emit('connected');
        sendInitialMessages();
        resolve();
      };

      ws.onerror = (error) => {
        // console.error('❌ [WebSocket] Connection error:', error);
        isConnecting = false;
        emit('error', error);
        reject(error);
      };

    } catch (error) {
      console.error('❌ [WebSocket] Connection failed:', error);
      isConnecting = false;
      reject(error);
    }
  });
}

function setupEventHandlers(): void {
  if (!ws) return;

  ws.onmessage = async (event) => {
    try {
      requestSeed++;
      const requestId = requestSeed;
      
      // Handle binary data decompression (like old frontend)
      let result;
      if (event.data instanceof ArrayBuffer) {
        result = await decompressBinaryData(event.data);
      } else {
        result = event.data;
      }
      
      if (!result) return;
      
      const data = convertObj(result);
      if (!data) return;
      
      if (ws && ws.readyState === WebSocket.OPEN) {
        requestProcessed = true;
        socketQueue.push({
          MessageType: data.MessageType,
          data: convertObj(data),
          requestId: requestId
        });
        requestProcessed = false;
      }
    } catch (error) {
      console.error('Error processing WebSocket message:', error);
    }
  };

  ws.onclose = (event) => {
    // console.log('🔌 [WebSocket] Disconnected - Code:', event.code, 'Reason:', event.reason);
    isConnecting = false;
    stopHeartbeat();
    stopConnectionStatus();
    emit('disconnected', event);
    
    // Handle specific error codes
    if (event.code === 1006) {
      // console.warn('⚠️ [WebSocket] Closed abnormally (1006) - likely server issue or maintenance');
      // Don't reconnect immediately for abnormal closures
      if (!_isMaintenanceMode) {
        handleReconnect();
      }
    } else if (event.code === 1000) {
      // console.log('✅ [WebSocket] Closed normally');
      // Normal closure, don't reconnect
    } else if (event.code !== 1000) {
      // console.warn(`⚠️ [WebSocket] Closed with code ${event.code}: ${event.reason}`);
      handleReconnect();
    }
  };
}

function handleReconnect(): void {
  // Don't reconnect if in maintenance mode
  if (_isMaintenanceMode) {
    console.log('Skipping reconnection - site is under maintenance');
    return;
  }

  const reConnectTime = reconnectSettings?.reconnection_time 
    ? Number(reconnectSettings.reconnection_time) * 1000 
    : reconnectDelay;
  const reConnectCnt = reconnectSettings?.reconnection_count || maxReconnectAttempts;

  if (reconnectAttempts >= reConnectCnt) {
    // console.error('Max reconnection attempts reached');
    emit('maxReconnectAttemptsReached');
    return;
  }

  reconnectAttempts++;
  const delay = reConnectTime;
  
  
  setTimeout(() => {
    connect().catch(console.error);
  }, delay);
}

function handleMessage(data: any): void {
  const messageType = data.MessageType;
  
  if (messageType) {
    emit(messageType, data);
  }
  
  // Handle notifications for all message types
  notificationService.handleSocketNotification(messageType, data);
  
  // Enhanced message handling with all types from old frontend
  switch (messageType) {
    case 'ForceLogout':
      handleForceLogout(data);
      break;
    case 'Logout':
      handleLogout(data);
      break;
    case 'AutoLogoutWarning':
      handleAutoLogoutWarning(data);
      break;
    case 'StopLogout':
      handleStopLogout(data);
      break;
    case 'PublicLogin':
      handlePublicLogin(data);
      break;
    case 'repair_site':
      handleRepairSite(data);
      break;
    case 'game_list':
      handleGameList(data);
      break;
    case 'live_status':
      handleLiveStatus(data);
      break;
    case 'powerball_status':
      handlePowerballStatus(data);
      break;
    case 'powerball_finished_bets':
      handlePowerballFinishedBets(data);
      break;
    case 'price_rate':
      handlePriceRate(data);
      break;
    case 'price_rate_virtual':
      handlePriceRateVirtual(data);
      break;
    case 'noticeList':
      handleNoticeList(data);
      break;
    case 'questionList':
      handleQuestionList(data);
      break;
    case 'noteList':
    case 'messageList':
    case 'notesList':
      handleNoteList(data);
      break;
    case 'notice':
      handleNotice(data);
      break;
    case 'gameCnt':
    case 'gameCntArr':
      handleGameCnt(data);
      break;
    case 'game_setting':
      handleGameSetting(data);
      break;
    case 'bet_setting':
      handleBetSetting(data);
      break;
    case 'Sport_Data':
      handleSportData(data);
      break;
    case 'market_setting':
      handleMarketSetting(data);
      break;
    case 'baseline_setting':
      handleBaselineSetting(data);
      break;
    case 'provider_setting':
      handleProviderSetting(data);
      break;
    case 'reconnect_setting':
      handleReconnectSetting(data);
      break;
    case 'delay_setting':
      handleDelaySetting(data);
      break;
    case 'betgame_status':
      handleBetGameSetting(data);
      break;
    case 'Connect':
      handleConnect(data);
      break;
    case 'DuplicateSocketWarning':
      handleDuplicateSocketWarning(data);
      break;
    case 'market_data':
      handleMarketData(data);
      break;
    case 'userInfo':
      handleUserInfo(data);
      break;
    // Legacy message types
    case 'GameList':
      emit('gameList', data);
      break;
    case 'ScoreData':
      emit('scoreData', data);
      break;
    case 'SportsList':
      emit('sportsList', data);
      break;
    case 'EventCount':
      emit('eventCount', data);
      break;
    case 'BetSetting':
      emit('betSetting', data);
      break;
    case 'MarketSetting':
      emit('marketSetting', data);
      break;
    case 'BaselineSetting':
      emit('baselineSetting', data);
      break;
    case 'ProviderSetting':
      emit('providerSetting', data);
      break;
    case 'ReconnectSetting':
      emit('reconnectSetting', data);
      break;
    case 'DelaySetting':
      emit('delaySetting', data);
      break;
    case 'GameCnt':
      emit('gameCnt', data);
      break;
    case 'RepairSite':
      emit('repairSite', data);
      break;
    default:
      emit('message', data);
  }
}

// Enhanced utility methods
async function decompressBinaryData(data: ArrayBuffer): Promise<string> {
  try {
    // Try to decompress using pako
    const decompressed = pako.inflate(new Uint8Array(data), { to: 'string' });
    return decompressed;
  } catch (err) {
    console.log('Binary decompression error:', err);
    // Fallback: try to decode as text
    try {
      const decoder = new TextDecoder();
      return decoder.decode(data);
    } catch (fallbackErr) {
      console.log('Fallback text decoding failed:', fallbackErr);
      return '';
    }
  }
}

function convertObj(data: any): any {
  try {
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    return data;
  } catch (error) {
    console.error('Error converting object:', error);
    return data;
  }
}

function isEmpty(value: any): boolean {
  return value === null || value === undefined || value === '' || 
         (Array.isArray(value) && value.length === 0) ||
         (typeof value === 'object' && Object.keys(value).length === 0);
}

// Message queue processor (like old frontend)
function startMessageQueueProcessor(): void {
  messageQueueProcessor = setInterval(() => {
    if (socketQueue.length > 0 && !requestProcessed) {
      const data = [...socketQueue];
      socketQueue = [];
      
      data.forEach(element => {
        if (element.MessageType === 'Sport_Data') {
          processSportData(element);
        } else {
          handleMessage(element.data);
        }
      });
    }
  }, 50);
}

function stopMessageQueueProcessor(): void {
  if (messageQueueProcessor) {
    clearInterval(messageQueueProcessor);
    messageQueueProcessor = null;
  }
}

// Heartbeat and connection monitoring
function startHeartbeat(): void {
  heartbeatInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      send({ MessageType: 'Connect', data: 'Status' });
    }
  }, 5000);
}

function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

function startConnectionStatus(): void {
  connectionStatusInterval = setInterval(() => {
    if (heartBitTime !== 0 && Date.now() - heartBitTime > 20000) {
      try {
        if (ws) {
          ws.close();
        }
      } catch (err) {
        console.log('Connection status error:', err);
      }
      heartBitTime = 0;
      return;
    }
  }, 1000);
}

function stopConnectionStatus(): void {
  if (connectionStatusInterval) {
    clearInterval(connectionStatusInterval);
    connectionStatusInterval = null;
  }
}

// Initial message sending
function sendInitialMessages(): void {
  if (token) {
    sendPublicLogin(token);
  }
}

// Enhanced message handlers
function handleForceLogout(data: any): void {
  emit('forceLogout', data);
  console.warn('Force logout due to server request');
}

function handleLogout(data: any): void {
  sendPublicLogin(token || '');
  emit('logout', data);
}

function handleAutoLogoutWarning(data: any): void {
  emit('autoLogoutWarning', data);
  console.warn('Auto logout warning');
}

function handleStopLogout(data: any): void {
  emit('stopLogout', data);
}

function handlePublicLogin(data: any): void {
  emit('publicLogin', data);
  if (data.status) {
    console.log('Public login status:', data.message);
  }
}

function handleRepairSite(data: any): void {
  emit('repairSite', data);
  if (data.state === 0) {
    maintenanceMode = false;
    _isMaintenanceMode = false;
    // Reset reconnection attempts when maintenance ends
    reconnectAttempts = 0;
    console.log('Maintenance mode ended - reconnection attempts reset');
  } else {
    maintenanceMode = true;
    _isMaintenanceMode = true;
    console.warn('Site under maintenance - stopping reconnection attempts');
    // Stop reconnection attempts during maintenance
    reconnectAttempts = maxReconnectAttempts;
  }
}

function handleGameList(data: any): void {
  emit('gameList', data);
}

function handleLiveStatus(data: any): void {
  emit('liveStatus', data);
}

function handlePowerballStatus(data: any): void {
  emit('powerballStatus', data);
}

function handlePowerballFinishedBets(data: any): void {
  emit('powerballFinishedBets', data);
}

function handlePriceRate(data: any): void {
  emit('priceRate', data);
}

function handlePriceRateVirtual(data: any): void {
  emit('priceRateVirtual', data);
}

function handleNoticeList(data: any): void {
  emit('noticeList', data);
  if (data.status) {
    console.info('New notice available');
  }
}

function handleQuestionList(data: any): void {
  emit('questionList', data);
  if (data.status) {
    console.info('New question available');
  }
}

function handleNoteList(data: any): void {
  emit('noteList', data);
  if (data?.status && data?.message) {
    const title = data.message.title || data.message.message || 'New message';
    const body = data.message.content || data.message.description || 'You have a new notification.';
    notificationService.notice(title, body, 6000);
  }
}

function handleNotice(data: any): void {
  emit('notice', data);
  // Handle different notice types
  if (data.bet) {
    data.bet.forEach((message: string) => {
      console.info('Bet notice:', message);
    });
  }
  if (data.deposit) {
    data.deposit.forEach((message: string) => {
      console.info('Deposit notice:', message);
    });
  }
}

function handleGameCnt(data: any): void {
  emit('gameCnt', data);
}

function handleGameSetting(data: any): void {
  emit('gameSetting', data);
}

function handleBetSetting(data: any): void {
  emit('betSetting', data);
}

function handleSportData(data: any): void {
  emit('sportData', data);
}

function processSportData(element: any): void {
  const itemObject = convertObj(element.data.data);
  
  if (isEmpty(sportsQueue[itemObject.fixtureId])) {
    sportsQueue[itemObject.fixtureId] = {};
  }
  
  sportsQueue[itemObject.fixtureId] = {
    data: [
      ...(sportsQueue[itemObject.fixtureId].data || []),
      ...itemObject.changes
    ],
    marketCount: itemObject.marketCount,
    requestId: element.requestId
  };
}

function handleMarketSetting(data: any): void {
  emit('marketSetting', data);
}

function handleBaselineSetting(data: any): void {
  emit('baselineSetting', data);
}

function handleProviderSetting(data: any): void {
  emit('providerSetting', data);
}

function handleReconnectSetting(data: any): void {
  reconnectSettings = data;
  emit('reconnectSetting', data);
}

function handleDelaySetting(data: any): void {
  emit('delaySetting', data);
}

function handleBetGameSetting(data: any): void {
  emit('betGameSetting', data);
}

function handleConnect(data: any): void {
  heartBitTime = Date.now();
  emit('connect', data);
}

function handleDuplicateSocketWarning(data: any): void {
  duplicateConnectionWarning = true;
  emit('duplicateSocketWarning', data);
  send({ MessageType: 'DuplicateWarningResponse', data: '' });
}

function handleMarketData(data: any): void {
  emit('marketData', data);
}

function handleUserInfo(data: any): void {
  emit('userInfo', data);
}

export function send(message: any): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(message));
  } else {
    console.warn('WebSocket is not connected');
  }
}

export function sendGameList(gameList: number[]): void {
  send({
    MessageType: 'GameList',
    data: gameList
  });
}

export function sendPublicLogin(token: string): void {
  send({
    MessageType: 'PublicLogin',
    token
  });
  // Mark that we sent PublicLogin so notification service can ignore our own responses
  notificationService.markPublicLoginSent();
  // Emit event so App can track when we send PublicLogin
  emit('publicLoginSent', { timestamp: Date.now() });
}

export function sendRoomType(pageInfo: any): void {
  send({
    MessageType: 'RoomType',
    data: pageInfo
  });
}

export function sendConnect(): void {
  send({
    MessageType: 'Connect',
    data: 'Status'
  });
}

export function sendDuplicateWarningResponse(): void {
  send({
    MessageType: 'DuplicateWarningResponse',
    data: ''
  });
}

export function on(event: string, handler: Function): void {
  if (!messageHandlers.has(event)) {
    messageHandlers.set(event, []);
  }
  messageHandlers.get(event)!.push(handler);
}

export function off(event: string, handler?: Function): void {
  if (!messageHandlers.has(event)) return;
  
  if (handler) {
    const handlers = messageHandlers.get(event)!;
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  } else {
    messageHandlers.delete(event);
  }
}

function emit(event: string, data?: any): void {
  const handlers = messageHandlers.get(event);
  if (handlers) {
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in WebSocket event handler for ${event}:`, error);
      }
    });
  }
}

export function disconnect(): void {
  if (ws) {
    ws.close(1000, 'Manual disconnect');
    ws = null;
  }
  stopHeartbeat();
  stopConnectionStatus();
  stopMessageQueueProcessor();
  messageHandlers.clear();
  socketQueue = [];
  sportsQueue = {};
  workFixtureIds = {};
  heartBitTime = 0;
  requestProcessed = false;
  duplicateConnectionWarning = false;
  maintenanceMode = false;
}

export function getConnectionState(): number {
  return ws ? ws.readyState : WebSocket.CLOSED;
}

export function isConnected(): boolean {
  return ws ? ws.readyState === WebSocket.OPEN : false;
}

export function getWebSocket(): WebSocket | null {
  return ws;
}

export function updateToken(newToken: string): void {
  token = newToken;
  if (isConnected()) {
    disconnect();
    connect();
  }
}

// Enhanced status and utility methods
export function isMaintenanceMode(): boolean {
  return maintenanceMode;
}

export function hasDuplicateConnectionWarning(): boolean {
  return duplicateConnectionWarning;
}

export function getSocketQueueLength(): number {
  return socketQueue.length;
}

export function getSportsQueueKeys(): string[] {
  return Object.keys(sportsQueue);
}

export function clearSportsQueue(): void {
  sportsQueue = {};
}

export function getReconnectSettings(): any {
  return reconnectSettings;
}

export function setBinaryType(type: 'arraybuffer' | 'blob'): void {
  binaryType = type;
  if (ws) {
    ws.binaryType = type;
  }
}

export function getBinaryType(): string {
  return binaryType;
}

export function getHeartBitTime(): number {
  return heartBitTime;
}

export function getRequestSeed(): number {
  return requestSeed;
}

export function resetRequestSeed(): void {
  requestSeed = 0;
}

// Enhanced event emission with more specific events
export function onForceLogout(handler: Function): void {
  on('forceLogout', handler);
}

export function onAutoLogoutWarning(handler: Function): void {
  on('autoLogoutWarning', handler);
}

export function onMaintenanceMode(handler: Function): void {
  on('repairSite', handler);
}

export function onDuplicateConnection(handler: Function): void {
  on('duplicateSocketWarning', handler);
}

export function onPriceUpdate(handler: Function): void {
  on('priceRate', handler);
}

export function onLiveScoreUpdate(handler: Function): void {
  on('liveStatus', handler);
}

export function onSportDataUpdate(handler: Function): void {
  on('sportData', handler);
}

export function onNoticeUpdate(handler: Function): void {
  on('notice', handler);
}

export function onUserInfoUpdate(handler: Function): void {
  on('userInfo', handler);
}

export function onQuestionListUpdate(handler: Function): void {
  on('questionList', handler);
}

export function onNoteListUpdate(handler: Function): void {
  on('noteList', handler);
}

/**
 * Trigger noteList event (e.g. when received via Socket.IO from main backend).
 * Backend sends noteList via Socket.IO; frontend main app uses raw WebSocket.
 * So we need to push noteList into the same handler pipeline when received elsewhere.
 * (In-app notification is shown from SocketContext when socket receives noteList.)
 */
export function triggerNoteList(data: any): void {
  const handlers = messageHandlers.get('noteList');
  if (handlers) {
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in noteList handler:', error);
      }
    });
  }
}

// Default export for backward compatibility
const websocketService = {
  connect,
  disconnect,
  send,
  sendGameList,
  sendPublicLogin,
  sendRoomType,
  sendConnect,
  sendDuplicateWarningResponse,
  on,
  off,
  getConnectionState,
  isConnected,
  getWebSocket,
  updateToken,
  isMaintenanceMode,
  hasDuplicateConnectionWarning,
  getSocketQueueLength,
  getSportsQueueKeys,
  clearSportsQueue,
  getReconnectSettings,
  setBinaryType,
  getBinaryType,
  getHeartBitTime,
  getRequestSeed,
  resetRequestSeed,
  onForceLogout,
  onAutoLogoutWarning,
  onMaintenanceMode,
  onDuplicateConnection,
  onPriceUpdate,
  onLiveScoreUpdate,
  onSportDataUpdate,
  onNoticeUpdate,
  onUserInfoUpdate,
  onQuestionListUpdate,
  onNoteListUpdate,
  triggerNoteList
};

export default websocketService;
